import React from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";
import Svg, { Circle, Rect } from "react-native-svg";
import { useTheme } from "../../../context/ThemeContext";
import { FONT_DISPLAY } from "./_shared";

/**
 * Single source of truth for the Voxcraft brand-mark + wordmark across the
 * marketing site, auth surface, and wizard rails. Mark is the VL-05 design —
 * five audio bars inside a circle — drawn as an inline SVG so it scales
 * cleanly at any density and stays a single brand color.
 *
 * Props:
 *   size         pixel size of the mark (square). Default 30 — the same
 *                30×30 chip used in v4's nav.
 *   color        mark color. Default theme.brand2.
 *   nameColor    wordmark color. Default theme.text.
 *   fontSize     wordmark size. Default scales with the mark (size * 0.6).
 *   showName     whether to render the "Voxcraft" wordmark. Default true.
 *   gap          space between mark and wordmark. Default 10.
 *   onPress      optional. If provided the whole row becomes pressable.
 *   style        passthrough style on the outer container.
 */
function NtVoxcraftBrand({
  size = 30,
  color,
  nameColor,
  fontSize,
  showName = true,
  gap = 10,
  onPress,
  style,
}) {
  const { theme } = useTheme();
  const markColor = color || theme.brand2;
  const textColor = nameColor || theme.text;
  const labelSize = fontSize || Math.round(size * 0.6);

  const inner = (
    <View style={[styles.row, { gap }, style]}>
      <Mark size={size} color={markColor} />
      {showName ? (
        <Text style={[styles.name, { color: textColor, fontSize: labelSize }]}>Voxcraft</Text>
      ) : null}
    </View>
  );

  if (onPress) {
    return (
      <Pressable onPress={onPress} style={({ pressed }) => [pressed && styles.pressed]}>
        {inner}
      </Pressable>
    );
  }
  return inner;
}

// =============================================================================
// Mark — exported so consumers that want the icon without the wordmark (e.g.
// favicon-style chip, hardware label) can render it solo.
// =============================================================================
export function Mark({ size = 30, color = "#2570BD" }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 60 60" fill="none">
      <Circle cx={30} cy={30} r={24} stroke={color} strokeWidth={2.5} fill="none" />
      <Rect x={18} y={24} width={3} height={12} rx={1.5} fill={color} />
      <Rect x={24} y={18} width={3} height={24} rx={1.5} fill={color} />
      <Rect x={30} y={22} width={3} height={16} rx={1.5} fill={color} />
      <Rect x={36} y={14} width={3} height={32} rx={1.5} fill={color} />
      <Rect x={42} y={26} width={3} height={8}  rx={1.5} fill={color} />
    </Svg>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: "row", alignItems: "center" },
  name: { fontFamily: FONT_DISPLAY, fontWeight: "800", letterSpacing: -0.5 },
  pressed: { opacity: 0.85 },
});

export default NtVoxcraftBrand;
